// usage_metrics.jsx
// PR Local usage-metrics dashboard — the PR Local counterpart of NAVELA's
// metrics_manager. Read-only reporting over the PR Local metric logs
// (prlocal_app_metrics / prlocal_map_metrics / viator_click_events, written by
// the PR Local golang backend) via the admin backend's prlocal-metrics package.
// Admins pick a date window + platform and can see, break down and filter:
// top-line usage, a trend over time, which Viator EXPERIENCES get the most
// affiliate clicks (by surface: card / featured / map_toast / saved / detail),
// which screens & targets get used, and a raw, filterable event explorer.
//
// Dataviz notes: magnitude is a single-hue (violet) bar length; the Viator
// surface split is the one categorical encoding — a fixed 5-hue order (validated
// colorblind-safe, worst adjacent CVD ΔE 9.1) always shown with a legend + direct
// labels so identity/magnitude never rely on colour alone; KPIs are plain ink
// stat tiles; change-over-time is a single-series bar trend with per-bar hover.

import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  BarChart3, Ticket, MousePointerClick, Eye, Search, RefreshCw, ChevronLeft, ChevronRight,
  Layers, Monitor, TrendingUp, ListFilter, Users, Map as MapIcon,
} from 'lucide-react';
import { usePrlocalMetrics } from '../../authentication_components/prlocalMetricsContext';
import { Modal, inputClass, Button, Loading, EmptyState } from './prlocal_ui';

// Fixed categorical order for the Viator-surface split. Validated colorblind-safe
// (worst adjacent CVD ΔE 9.1, normal-vision ΔE 19.6 on the light surface); always
// paired with a legend and text labels, so identity is never colour-alone.
const SURFACE_ORDER = ['card', 'featured', 'map_toast', 'saved', 'detail'];
const SURFACE_COLORS = {
  card: '#2a78d6', featured: '#008300', map_toast: '#e87ba4', saved: '#eda100', detail: '#1baf7a',
};
const SURFACE_LABELS = {
  card: 'Tarjeta', featured: 'Destacado', map_toast: 'Mapa', saved: 'Guardado', detail: 'Detalle',
};

const PLATFORMS = ['ios', 'android', 'web'];
const TREND_METRICS = [
  { id: 'events', label: 'Eventos' },
  { id: 'sessions', label: 'Sesiones' },
  { id: 'viator_clicks', label: 'Toques Viator' },
];
const EVENTS_PAGE_SIZE = 50;

// ── date helpers (windows are inclusive in the UI; the API upper bound is
// exclusive, so we send `to`+1 day). All in UTC — fine for admin reporting. ──
const isoDate = (d) => d.toISOString().slice(0, 10);
const addDays = (dateStr, n) => {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return isoDate(d);
};
const todayStr = () => isoDate(new Date());

const fmtInt = (n) => (n ?? 0).toLocaleString();
const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};
const fmtBucket = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export default function UsageMetrics() {
  const {
    fetchMetricsSummary, fetchMetricsFilters, fetchMetricsTimeseries,
    fetchExperienceClicks, fetchTargets, fetchScreenViews, fetchMetricsEvents,
  } = usePrlocalMetrics();

  // Global filters (drive every section).
  const [fromDate, setFromDate] = useState(() => addDays(todayStr(), -29));
  const [toDate, setToDate] = useState(() => todayStr());
  const [platform, setPlatform] = useState('');

  // Report data.
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [screens, setScreens] = useState([]);
  const [targets, setTargets] = useState([]);
  const [options, setOptions] = useState(null);

  // Section-local filters.
  const [trendMetric, setTrendMetric] = useState('events');
  const [expSurface, setExpSurface] = useState('');
  const [expCity, setExpCity] = useState('');
  const [targetType, setTargetType] = useState('');

  // Event explorer.
  const [evName, setEvName] = useState('');
  const [evScreen, setEvScreen] = useState('');
  const [evSearchInput, setEvSearchInput] = useState('');
  const [evSearch, setEvSearch] = useState('');
  const [evOffset, setEvOffset] = useState(0);
  const [eventsData, setEventsData] = useState({ events: [], total_count: 0 });
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Per-section loading flags.
  const [loadingCore, setLoadingCore] = useState(true);
  const [loadingExp, setLoadingExp] = useState(true);
  const [loadingTargets, setLoadingTargets] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);

  // API window: from as-is, to made exclusive (+1 day) so the end day is included.
  const apiRange = useMemo(() => ({ from: fromDate, to: addDays(toDate, 1) }), [fromDate, toDate]);

  // Filter option lists (loaded once).
  useEffect(() => {
    fetchMetricsFilters()
      .then((data) => setOptions(data || null))
      .catch(() => { /* dropdowns simply stay minimal */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Core: summary + trend + screen views (global filters only).
  useEffect(() => {
    let cancelled = false;
    setLoadingCore(true);
    Promise.all([
      fetchMetricsSummary({ from: apiRange.from, to: apiRange.to, platform: platform || undefined }),
      fetchMetricsTimeseries({ from: apiRange.from, to: apiRange.to, interval: 'day', platform: platform || undefined }),
      fetchScreenViews({ from: apiRange.from, to: apiRange.to, platform: platform || undefined }),
    ])
      .then(([sum, ts, scr]) => {
        if (cancelled) return;
        setSummary(sum || null);
        setSeries(Array.isArray(ts) ? ts : []);
        setScreens(Array.isArray(scr) ? scr : []);
      })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Error al cargar métricas'); })
      .finally(() => { if (!cancelled) setLoadingCore(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRange.from, apiRange.to, platform]);

  // Viator experience clicks (window + surface/city). Not platform-attributed.
  useEffect(() => {
    let cancelled = false;
    setLoadingExp(true);
    fetchExperienceClicks({
      from: apiRange.from, to: apiRange.to,
      surface: expSurface || undefined, city: expCity || undefined, limit: 25,
    })
      .then((data) => { if (!cancelled) setExperiences(Array.isArray(data) ? data : []); })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Error al cargar toques de Viator'); })
      .finally(() => { if (!cancelled) setLoadingExp(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRange.from, apiRange.to, expSurface, expCity]);

  // Top targets (global filters + target type).
  useEffect(() => {
    let cancelled = false;
    setLoadingTargets(true);
    fetchTargets({ from: apiRange.from, to: apiRange.to, platform: platform || undefined, target_type: targetType || undefined })
      .then((data) => { if (!cancelled) setTargets(Array.isArray(data) ? data : []); })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Error al cargar destinos'); })
      .finally(() => { if (!cancelled) setLoadingTargets(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRange.from, apiRange.to, platform, targetType]);

  // Debounce the event-explorer free-text search.
  useEffect(() => {
    const t = setTimeout(() => { setEvOffset(0); setEvSearch(evSearchInput.trim()); }, 350);
    return () => clearTimeout(t);
  }, [evSearchInput]);

  // Event explorer (global filters + its own filters + pagination).
  useEffect(() => {
    let cancelled = false;
    setLoadingEvents(true);
    fetchMetricsEvents({
      from: apiRange.from, to: apiRange.to, platform: platform || undefined,
      event_name: evName || undefined, screen: evScreen || undefined, search: evSearch || undefined,
      limit: EVENTS_PAGE_SIZE, offset: evOffset,
    })
      .then((data) => { if (!cancelled) setEventsData(data || { events: [], total_count: 0 }); })
      .catch((err) => { if (!cancelled) toast.error(err.message || 'Error al cargar eventos'); })
      .finally(() => { if (!cancelled) setLoadingEvents(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRange.from, apiRange.to, platform, evName, evScreen, evSearch, evOffset]);

  const applyPreset = (days) => { setFromDate(addDays(todayStr(), -(days - 1))); setToDate(todayStr()); };
  const refreshAll = () => {
    // Explicit reload of every section (works even when filters are unchanged, so
    // the effects wouldn't otherwise re-fire).
    setLoadingCore(true); setLoadingExp(true); setLoadingTargets(true); setLoadingEvents(true);
    Promise.allSettled([
      fetchMetricsSummary({ from: apiRange.from, to: apiRange.to, platform: platform || undefined }).then((d) => setSummary(d || null)),
      fetchMetricsTimeseries({ from: apiRange.from, to: apiRange.to, interval: 'day', platform: platform || undefined }).then((d) => setSeries(Array.isArray(d) ? d : [])),
      fetchScreenViews({ from: apiRange.from, to: apiRange.to, platform: platform || undefined }).then((d) => setScreens(Array.isArray(d) ? d : [])),
      fetchExperienceClicks({ from: apiRange.from, to: apiRange.to, surface: expSurface || undefined, city: expCity || undefined, limit: 25 }).then((d) => setExperiences(Array.isArray(d) ? d : [])),
      fetchTargets({ from: apiRange.from, to: apiRange.to, platform: platform || undefined, target_type: targetType || undefined }).then((d) => setTargets(Array.isArray(d) ? d : [])),
      fetchMetricsEvents({ from: apiRange.from, to: apiRange.to, platform: platform || undefined, event_name: evName || undefined, screen: evScreen || undefined, search: evSearch || undefined, limit: EVENTS_PAGE_SIZE, offset: evOffset }).then((d) => setEventsData(d || { events: [], total_count: 0 })),
    ]).finally(() => { setLoadingCore(false); setLoadingExp(false); setLoadingTargets(false); setLoadingEvents(false); });
  };

  const screenOptions = options?.screens || [];
  const cityOptions = options?.municipalities || [];
  const targetTypeOptions = options?.target_types || [];
  const eventNameOptions = options?.event_names || [];

  const evPage = Math.floor(evOffset / EVENTS_PAGE_SIZE) + 1;
  const evPageCount = Math.max(1, Math.ceil((eventsData.total_count || 0) / EVENTS_PAGE_SIZE));

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      {/* Heading */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800">
            <BarChart3 className="h-5 w-5 text-violet-600" /> Métricas de Uso
          </h2>
          <p className="text-sm text-slate-500">Analítica de producto de primera mano de la app PR Local &amp; la web.</p>
        </div>
        <Button variant="secondary" onClick={refreshAll} title="Actualizar">
          <RefreshCw className={`h-4 w-4 ${loadingCore ? 'animate-spin' : ''}`} /> Actualizar
        </Button>
      </div>

      {/* Global filter row */}
      <div className="mb-6 flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Desde</span>
          <input type="date" value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} className={`${inputClass} w-40`} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Hasta</span>
          <input type="date" value={toDate} min={fromDate} max={todayStr()} onChange={(e) => setToDate(e.target.value)} className={`${inputClass} w-40`} />
        </label>
        <div className="flex items-center gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => applyPreset(d)}
              className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-50 hover:text-violet-600">
              {d}d
            </button>
          ))}
        </div>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Plataforma</span>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} className={`${inputClass} w-40`}>
            <option value="">Todas</option>
            {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
      </div>

      {/* KPI stat tiles */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile label="Eventos" value={summary?.events} icon={Layers} loading={loadingCore} />
        <StatTile label="Sesiones" value={summary?.sessions} icon={TrendingUp} loading={loadingCore} />
        <StatTile label="Usuarios" value={summary?.users} icon={Users} loading={loadingCore} />
        <StatTile label="Vistas de pantalla" value={summary?.screen_views} icon={Monitor} loading={loadingCore} />
        <StatTile label="Interacciones de mapa" value={summary?.map_events} icon={MapIcon} loading={loadingCore} />
        <StatTile label="Toques Viator" value={summary?.viator_clicks} icon={Ticket} loading={loadingCore} accent />
      </div>

      {/* Trend */}
      <Section
        title="Actividad en el tiempo"
        icon={TrendingUp}
        right={
          <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5">
            {TREND_METRICS.map((m) => (
              <button key={m.id} onClick={() => setTrendMetric(m.id)}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
                  trendMetric === m.id ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>
                {m.label}
              </button>
            ))}
          </div>
        }
      >
        {loadingCore ? <Loading label="Cargando tendencia…" />
          : <TrendBars data={series} metric={trendMetric} />}
      </Section>

      {/* Viator experience clicks */}
      <Section
        title="Experiencias Viator con más toques de afiliado"
        icon={Ticket}
        right={
          <div className="flex flex-wrap items-center gap-2">
            <SurfaceLegend />
            <select value={expSurface} onChange={(e) => setExpSurface(e.target.value)} className={`${inputClass} h-9 w-36 py-1`}>
              <option value="">Cualquier origen</option>
              {SURFACE_ORDER.map((s) => <option key={s} value={s}>{SURFACE_LABELS[s]}</option>)}
            </select>
            {cityOptions.length > 0 && (
              <select value={expCity} onChange={(e) => setExpCity(e.target.value)} className={`${inputClass} h-9 w-40 py-1`}>
                <option value="">Cualquier municipio</option>
                {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>
        }
      >
        <p className="mb-3 text-xs text-slate-400">Los toques de Viator no se filtran por plataforma (no llevan esa dimensión).</p>
        {loadingExp ? <Loading label="Cargando toques de Viator…" />
          : experiences.length === 0 ? <EmptyState icon={Ticket} title="Sin toques en este periodo" hint="Los toques aparecen aquí cuando un usuario abre una experiencia de Viator." />
            : <ExperienceClickTable experiences={experiences} />}
      </Section>

      {/* Screens + targets */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="Pantallas más vistas" icon={Eye}>
          {loadingCore ? <Loading label="Cargando pantallas…" />
            : screens.length === 0 ? <EmptyState icon={Eye} title="Sin vistas de pantalla todavía" />
              : <MagnitudeList items={screens.slice(0, 12).map((s) => ({ label: s.screen || '(desconocida)', value: s.views }))} />}
        </Section>

        <Section
          title="Destinos con más interacción"
          icon={MousePointerClick}
          right={
            targetTypeOptions.length > 0 && (
              <select value={targetType} onChange={(e) => setTargetType(e.target.value)} className={`${inputClass} h-9 w-44 py-1`}>
                <option value="">Todos los tipos</option>
                {targetTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )
          }
        >
          {loadingTargets ? <Loading label="Cargando destinos…" />
            : targets.length === 0 ? <EmptyState icon={MousePointerClick} title="Sin interacción con destinos todavía" />
              : <MagnitudeList items={targets.slice(0, 12).map((t) => ({
                  label: t.target_name || t.target_id || '(desconocido)',
                  sub: [t.target_type, t.target_category, t.municipality].filter(Boolean).join(' · '),
                  value: t.count,
                }))} />}
        </Section>
      </div>

      {/* Raw event explorer */}
      <Section title="Explorador de eventos" icon={ListFilter}>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={evSearchInput} onChange={(e) => setEvSearchInput(e.target.value)}
              placeholder="Buscar pantalla, destino, categoría, municipio…" className={`${inputClass} pl-9`} />
          </div>
          <select value={evName} onChange={(e) => { setEvOffset(0); setEvName(e.target.value); }} className={`${inputClass} w-52`}>
            <option value="">Cualquier evento</option>
            {(eventNameOptions.length ? eventNameOptions : ['screen_viewed', 'profile_viewed', 'search_submitted', 'directions_tapped', 'share_tapped', 'login_completed', 'signup_completed']).map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          {screenOptions.length > 0 && (
            <select value={evScreen} onChange={(e) => { setEvOffset(0); setEvScreen(e.target.value); }} className={`${inputClass} w-44`}>
              <option value="">Cualquier pantalla</option>
              {screenOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>

        {loadingEvents && (eventsData.events || []).length === 0 ? (
          <Loading label="Cargando eventos…" />
        ) : (eventsData.events || []).length === 0 ? (
          <EmptyState icon={ListFilter} title="Ningún evento coincide con estos filtros" hint="Amplía el rango de fechas o quita un filtro." />
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Evento</th>
                    <th className="px-4 py-3">Plataforma</th>
                    <th className="px-4 py-3">Pantalla</th>
                    <th className="px-4 py-3">Destino</th>
                    <th className="px-4 py-3">Cuándo</th>
                    <th className="px-4 py-3 text-right">Detalle</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {(eventsData.events || []).map((e, idx) => (
                    <tr key={e.client_event_id || `${e.client_timestamp}-${idx}`} className="hover:bg-slate-50/60">
                      <td className="px-4 py-3"><EventBadge name={e.event_name} /></td>
                      <td className="px-4 py-3 text-slate-600">{e.platform || <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <div className="font-medium text-slate-700">{e.screen_name || <span className="text-slate-300">—</span>}</div>
                        {e.event_category && <div className="text-xs text-slate-400">{e.event_category}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {e.target_name || e.target_id ? (
                          <div>
                            <div className="font-medium text-slate-700">{e.target_name || e.target_id}</div>
                            {(e.target_type || e.target_municipality) && (
                              <div className="text-xs text-slate-400">{[e.target_type, e.target_municipality].filter(Boolean).join(' · ')}</div>
                            )}
                          </div>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-500">{fmtDateTime(e.client_timestamp)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setSelectedEvent(e)} className="rounded-lg p-2 text-slate-400 hover:bg-violet-50 hover:text-violet-600" title="Ver evento">
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
              <span>{fmtInt(eventsData.total_count)} evento{eventsData.total_count === 1 ? '' : 's'} · página {evPage} de {evPageCount}</span>
              <div className="flex items-center gap-1">
                <button disabled={evOffset === 0} onClick={() => setEvOffset(Math.max(0, evOffset - EVENTS_PAGE_SIZE))} className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
                <button disabled={evPage >= evPageCount} onClick={() => setEvOffset(evOffset + EVENTS_PAGE_SIZE)} className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        )}
      </Section>

      <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  );
}

// ── Presentational pieces ──────────────────────────────────────────────────────

function Section({ title, icon: Icon, right, children }) {
  return (
    <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-600">
          {Icon && <Icon className="h-4 w-4 text-violet-500" />} {title}
        </h3>
        {right && <div>{right}</div>}
      </div>
      {children}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, loading, accent }) {
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${accent ? 'border-violet-100 bg-violet-50/60' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        {Icon && <Icon className={`h-4 w-4 ${accent ? 'text-violet-500' : 'text-slate-300'}`} />}
      </div>
      <div className="mt-2 text-2xl font-extrabold tabular-nums text-slate-800">
        {loading ? <span className="inline-block h-7 w-16 animate-pulse rounded bg-slate-100" /> : fmtInt(value)}
      </div>
    </div>
  );
}

// Single-series bar trend (change-over-time). Single hue (magnitude), 2px gaps,
// rounded tops, per-bar hover tooltip. One axis only.
function TrendBars({ data, metric }) {
  if (!data || data.length === 0) {
    return <EmptyState icon={TrendingUp} title="Sin actividad en este periodo" hint="Elige un rango de fechas más amplio o una plataforma distinta." />;
  }
  const max = Math.max(1, ...data.map((d) => d[metric] || 0));
  return (
    <div>
      <div className="flex h-44 items-end gap-[2px]">
        {data.map((d, i) => {
          const v = d[metric] || 0;
          const pct = (v / max) * 100;
          return (
            <div key={d.bucket || i} className="group relative flex flex-1 items-end" style={{ minWidth: 2 }}>
              <div
                className="w-full rounded-t bg-violet-500 transition-colors group-hover:bg-violet-600"
                style={{ height: `${Math.max(v > 0 ? 2 : 0, pct)}%` }}
              />
              {/* hover tooltip */}
              <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-slate-800 px-2 py-1 text-xs text-white shadow-lg group-hover:block">
                <span className="font-semibold">{fmtInt(v)}</span> · {fmtBucket(d.bucket)}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between text-xs text-slate-400">
        <span>{fmtBucket(data[0].bucket)}</span>
        <span>{fmtBucket(data[data.length - 1].bucket)}</span>
      </div>
    </div>
  );
}

// Sequential magnitude list: label + single-hue bar + value. Bar length is the
// only magnitude encoding (one hue, never rank-coloured).
function MagnitudeList({ items }) {
  const max = Math.max(1, ...items.map((i) => i.value || 0));
  return (
    <ul className="space-y-2.5">
      {items.map((it, idx) => (
        <li key={`${it.label}-${it.sub || ''}-${idx}`}>
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <span className="truncate text-sm font-medium text-slate-700" title={it.sub ? `${it.label} · ${it.sub}` : it.label}>
              {it.label}
              {it.sub && <span className="ml-1.5 text-xs font-normal text-slate-400">{it.sub}</span>}
            </span>
            <span className="shrink-0 text-sm font-semibold tabular-nums text-slate-600">{fmtInt(it.value)}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-violet-500" style={{ width: `${Math.max(2, (it.value / max) * 100)}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

// The Viator-surface legend (always present for the 5-colour categorical set).
function SurfaceLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {SURFACE_ORDER.map((s) => (
        <span key={s} className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: SURFACE_COLORS[s] }} /> {SURFACE_LABELS[s]}
        </span>
      ))}
    </div>
  );
}

// Top-experiences table: clicks + a categorical stacked split by surface.
function ExperienceClickTable({ experiences }) {
  const maxClicks = Math.max(1, ...experiences.map((s) => s.clicks || 0));
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-100 text-sm">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3">Experiencia</th>
            <th className="px-4 py-3 w-1/3">Origen del toque</th>
            <th className="px-4 py-3 text-right">Toques</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {experiences.map((s, idx) => (
            <tr key={s.product_code || s.experience_id || idx} className="hover:bg-slate-50/60">
              <td className="px-4 py-3">
                <div className="font-semibold text-slate-800">{s.title || s.product_code || '(experiencia desconocida)'}</div>
                {(s.city || s.category) && <div className="text-xs text-slate-400">{[s.city, s.category].filter(Boolean).join(' · ')}</div>}
              </td>
              <td className="px-4 py-3">
                <StackedSurfaceBar bySurface={s.by_surface} total={s.clicks} />
              </td>
              <td className="px-4 py-3 text-right">
                <div className="text-sm font-bold tabular-nums text-slate-700">{fmtInt(s.clicks)}</div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-violet-300" style={{ width: `${Math.max(2, (s.clicks / maxClicks) * 100)}%` }} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Stacked categorical bar with a 2px surface gap between segments. Each nonzero
// segment carries a text count so identity/magnitude never rely on colour alone.
function StackedSurfaceBar({ bySurface, total }) {
  const bs = bySurface || {};
  const sum = Math.max(1, total || SURFACE_ORDER.reduce((a, k) => a + (bs[k] || 0), 0));
  const present = SURFACE_ORDER.filter((k) => (bs[k] || 0) > 0);
  return (
    <div>
      <div className="flex h-4 w-full items-stretch gap-[2px] overflow-hidden rounded">
        {present.map((k) => (
          <div key={k} title={`${SURFACE_LABELS[k]}: ${fmtInt(bs[k])}`} style={{ backgroundColor: SURFACE_COLORS[k], flexGrow: bs[k], flexBasis: 0 }} className="first:rounded-l last:rounded-r" />
        ))}
        {present.length === 0 && <div className="w-full rounded bg-slate-100" />}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
        {SURFACE_ORDER.filter((k) => (bs[k] || 0) > 0).map((k) => (
          <span key={k} className="tabular-nums">
            <span className="font-medium" style={{ color: SURFACE_COLORS[k] }}>{SURFACE_LABELS[k]}</span> {fmtInt(bs[k] || 0)}
            <span className="text-slate-400"> ({Math.round(((bs[k] || 0) / sum) * 100)}%)</span>
          </span>
        ))}
      </div>
    </div>
  );
}

const EVENT_BADGE_STYLES = {
  screen_viewed: 'bg-slate-100 text-slate-600 ring-slate-200',
  profile_viewed: 'bg-sky-50 text-sky-700 ring-sky-100',
  search_submitted: 'bg-amber-50 text-amber-700 ring-amber-100',
  directions_tapped: 'bg-violet-50 text-violet-700 ring-violet-100',
  share_tapped: 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-100',
  login_completed: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  signup_completed: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  logout: 'bg-slate-100 text-slate-500 ring-slate-200',
  suggestion_submitted: 'bg-teal-50 text-teal-700 ring-teal-100',
};

function EventBadge({ name }) {
  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${EVENT_BADGE_STYLES[name] || 'bg-slate-100 text-slate-600 ring-slate-200'}`}>
      {name || '—'}
    </span>
  );
}

function EventDetailModal({ event, onClose }) {
  if (!event) return null;
  const rows = [
    ['Evento', event.event_name],
    ['Categoría', event.event_category],
    ['Plataforma', event.platform],
    ['Versión de app', event.app_version],
    ['Versión de SO', event.os_version],
    ['Modelo', event.device_model],
    ['Pantalla', event.screen_name],
    ['Tipo de destino', event.target_type],
    ['Destino', event.target_name || event.target_id],
    ['Categoría de destino', event.target_category],
    ['Municipio', event.target_municipality],
    ['Idioma', event.app_locale],
    ['Red', event.network_type],
    ['Sesión', event.session_id],
    ['Anónimo', event.anonymous_id],
    ['Usuario', event.user_id],
    ['Cuándo', fmtDateTime(event.client_timestamp)],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');

  let meta = null;
  if (event.properties) {
    try {
      meta = typeof event.properties === 'string' ? JSON.parse(event.properties) : event.properties;
    } catch {
      meta = null;
    }
  }
  const hasMeta = meta && typeof meta === 'object' && Object.keys(meta).length > 0;

  return (
    <Modal open title="Evento" subtitle={event.event_name} onClose={onClose} wide
      footer={<Button variant="secondary" onClick={onClose}>Cerrar</Button>}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {rows.map(([k, v]) => (
            <div key={k}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{k}</p>
              <p className="mt-0.5 break-words text-sm text-slate-700">{String(v)}</p>
            </div>
          ))}
        </div>
        {hasMeta && (
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Propiedades</p>
            <pre className="max-h-64 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              {JSON.stringify(meta, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </Modal>
  );
}
